import { useMemo } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarDays,
  Clock,
  Home,
  LogOut,
  type LucideIcon,
  ScanFace,
  ScrollText,
  Sparkles,
  User,
} from "lucide-react";

import { ErrorBoundary } from "@/components/error-boundary";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { logout } from "@/lib/auth";
import { ModuleCode, useCanUseAiAssistant, useMe } from "@/lib/me";
import type { User as UserT } from "@/lib/types";
import { cn } from "@/lib/utils";

interface MeNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  /** When set, the item only renders if the tenant has the module
   *  enabled. Same gate as the desktop sidebar uses. */
  requiresModule?: string;
  /** When true, the item also needs the per-user AI capability
   *  (the "AI User" role — see ``useCanUseAiAssistant``). */
  requiresAiRole?: boolean;
}

const ME_NAV: MeNavItem[] = [
  { to: "/me", label: "Home", icon: Home, end: true },
  { to: "/me/leave", label: "Leave", icon: CalendarDays },
  { to: "/me/payslips", label: "Payslips", icon: ScrollText },
  { to: "/me/attendance", label: "Time", icon: Clock },
  {
    to: "/me/assistant",
    label: "Assistant",
    icon: Sparkles,
    requiresModule: ModuleCode.COPILOT,
    requiresAiRole: true,
  },
  {
    to: "/me/check-in",
    label: "Check in",
    icon: ScanFace,
    requiresModule: ModuleCode.BIOMETRIC_ATTENDANCE,
  },
  { to: "/me/profile", label: "Profile", icon: User },
];

/** Self-service shell — guide §9. Mobile-first; sidebar/topbar are
 *  replaced with a top app bar and a fixed bottom nav. Targets stay ≥44px. */
export function MeLayout() {
  const navigate = useNavigate();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<UserT>("/accounts/users/me/"),
  });

  // Drives module-gated nav items (same gate the desktop sidebar uses).
  const meSession = useMe();
  const enabledModules = meSession.data?.enabled_modules ?? [];
  const canUseAi = useCanUseAiAssistant();
  const nav = useMemo(
    () =>
      ME_NAV.filter(
        (item) =>
          (!item.requiresModule ||
            enabledModules.includes(item.requiresModule)) &&
          (!item.requiresAiRole || canUseAi),
      ),
    [enabledModules, canUseAi],
  );

  function handleSignOut() {
    logout();
    navigate("/login", { replace: true });
  }

  const displayName = me.data
    ? `${me.data.first_name} ${me.data.last_name}`.trim() || me.data.username
    : "";

  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <header className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4">
        <Link
          to="/"
          aria-label="Go to dashboard"
          className="flex items-center gap-2"
        >
          <img
            src="/logo-icon.png"
            alt=""
            aria-hidden="true"
            className="h-7 w-7"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
          <span className="text-sm font-semibold tracking-tight text-navy">
            HRIS Workbench
          </span>
        </Link>
        <span className="ml-auto">
          <Avatar name={displayName} fallback="U" size={32} tone="blue" />
        </span>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Sign out"
          onClick={handleSignOut}
          className="text-slate"
        >
          <LogOut size={16} strokeWidth={1.5} aria-hidden="true" />
        </Button>
      </header>

      <main className="flex-1 px-4 pb-24 pt-4">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      <nav
        className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
        aria-label="Self-service navigation"
      >
        <ul className="mx-auto flex max-w-md items-stretch justify-around">
          {nav.map((item) => (
            <li key={item.to} className="flex-1">
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "flex h-14 flex-col items-center justify-center gap-1 text-[11px] font-medium",
                    isActive ? "text-blue" : "text-slate hover:text-navy",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      size={20}
                      strokeWidth={isActive ? 2 : 1.5}
                      aria-hidden="true"
                    />
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
